// FindingsTable — the scan's findings with an expandable evidence + remediation
// detail (why-this-matters, remediation guidance, explanation, vulnerability
// details, false-positive check, code evidence). Used for both the repo-level
// "global" findings and a component's own findings.
import { useMemo, useState, type Key } from "react";
import { Table, Typography, theme, Segmented } from "antd";
import type { TableColumnsType } from "antd";
import { ChevronDown, ChevronRight } from "lucide-react";
import { SeverityText } from "@/components/agate";
import { ToneTag, Th } from "./kit";
import { severityToTone, verdictToTone, type VMFinding } from "@/lib/api/scanReport";

// Confidence: the WORD carries the signal; LOW gets a dashed border as the
// distinct "don't trust this yet" cue.
function ConfidencePill({ confidence }: { confidence?: string }) {
  const low = (confidence || "").toLowerCase() === "low";
  return (
    <ToneTag tone="muted" dashed={low} title={low ? "Low confidence. Treat as unverified, not safe." : undefined}>
      {confidence || "—"}
    </ToneTag>
  );
}

const { Text, Paragraph } = Typography;
const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

const VERDICT_LABEL: Record<string, string> = {
  VULNERABLE: "vulnerable",
  SECURE: "secure",
  NOT_APPLICABLE: "n/a",
  UNKNOWN: "inconclusive",
};
const SEV_RANK: Record<string, number> = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
const VERDICT_RANK: Record<string, number> = { VULNERABLE: 0, UNKNOWN: 1, NOT_APPLICABLE: 2, SECURE: 3 };

function DetailBlock({ label, children }: { label: string; children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div>
      <div className="mb-0.5 text-[11px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="text-sm text-text-secondary">{children}</div>
    </div>
  );
}

function Expanded({ f }: { f: VMFinding }) {
  const { token } = theme.useToken();
  return (
    <div className="flex flex-col gap-3 px-2 py-2">
      <DetailBlock label="Why this matters">{f.why_this_matters}</DetailBlock>
      {f.fix_guidance && (
        <div
          className="rounded-lg p-3"
          style={{ background: token.colorFillQuaternary, border: `1px solid ${token.colorBorderSecondary}` }}
        >
          <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
            Remediation guidance
          </div>
          <div className="text-sm text-text-primary">{f.fix_guidance}</div>
        </div>
      )}
      <DetailBlock label="Explanation">{f.rationale}</DetailBlock>
      <DetailBlock label="Vulnerability details">{f.vulnerability_details}</DetailBlock>
      <DetailBlock label="False-positive check">{f.false_positive_check}</DetailBlock>
      {f.realized_severity && f.realized_severity.toUpperCase() !== (f.severity || "").toUpperCase() && (
        <DetailBlock label="Adjudicated severity">
          <SeverityText tone={severityToTone(f.realized_severity)}>{f.realized_severity}</SeverityText>{" "}
          <span className="text-text-muted">(raw {f.severity})</span>
        </DetailBlock>
      )}
      <DetailBlock label="Adjudication note">{f.reconciliation_note || f.context_note}</DetailBlock>
      {(f.evidence?.length ?? 0) > 0 && (
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wider text-text-muted">
            Evidence ({f.evidence!.length})
          </div>
          <div className="flex flex-col gap-2">
            {f.evidence!.map((e, i) => (
              <div
                key={i}
                className="overflow-hidden rounded-lg border border-border"
                style={{ background: token.colorBgLayout }}
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-3 py-1.5">
                  <span style={{ fontFamily: MONO, fontSize: 12 }} className="text-text-primary">
                    {e.path}
                  </span>
                  <span className="text-xs text-text-muted">
                    lines {e.start_line}–{e.end_line}
                  </span>
                  {e.match_type && <ToneTag tone="muted">{e.match_type}</ToneTag>}
                </div>
                <pre
                  className="m-0 overflow-x-auto px-3 py-2"
                  style={{ fontFamily: MONO, fontSize: 12, lineHeight: 1.6 }}
                >
                  {e.snippet}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FindingsTable({
  findings,
  showFilter = true,
}: {
  findings: VMFinding[];
  showFilter?: boolean;
}) {
  const { token } = theme.useToken();
  const [filter, setFilter] = useState<"all" | "vulnerable" | "secure" | "other">("all");
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

  const rows = useMemo(() => {
    const f =
      filter === "all"
        ? findings
        : filter === "vulnerable"
          ? findings.filter((x) => x.verdict === "VULNERABLE")
          : filter === "secure"
            ? findings.filter((x) => x.verdict === "SECURE")
            : findings.filter((x) => x.verdict === "UNKNOWN" || x.verdict === "NOT_APPLICABLE");
    return [...f].sort(
      (a, b) =>
        (VERDICT_RANK[a.verdict] ?? 9) - (VERDICT_RANK[b.verdict] ?? 9) ||
        (SEV_RANK[(a.severity || "").toUpperCase()] ?? 9) - (SEV_RANK[(b.severity || "").toUpperCase()] ?? 9),
    );
  }, [findings, filter]);

  const counts = useMemo(() => {
    let v = 0,
      s = 0,
      u = 0,
      na = 0;
    for (const f of findings) {
      if (f.verdict === "VULNERABLE") v++;
      else if (f.verdict === "SECURE") s++;
      else if (f.verdict === "NOT_APPLICABLE") na++;
      else u++;
    }
    return { v, s, u, na };
  }, [findings]);

  const CONF_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const columns: TableColumnsType<VMFinding> = [
    {
      title: Th("Verdict"),
      key: "verdict",
      width: 120,
      sorter: (a, b) => (VERDICT_RANK[a.verdict] ?? 9) - (VERDICT_RANK[b.verdict] ?? 9),
      render: (_, f) => (
        <ToneTag tone={verdictToTone(f.verdict)}>{VERDICT_LABEL[f.verdict] ?? f.verdict.toLowerCase()}</ToneTag>
      ),
    },
    {
      title: Th("Rule ID"),
      key: "rule_id",
      width: 130,
      sorter: (a, b) => (a.rule_id || "").localeCompare(b.rule_id || ""),
      render: (_, f) => (
        <Text style={{ fontFamily: MONO, fontSize: 12, color: token.colorTextTertiary }}>{f.rule_id}</Text>
      ),
    },
    { title: Th("Rule"), key: "rule", render: (_, f) => <Text className="text-sm">{f.rule_name || f.rule_id}</Text> },
    {
      title: Th("Severity"),
      key: "severity",
      width: 110,
      // Show the ADJUDICATED severity (realized_severity) — the raw rule severity
      // can overstate after context relief; raw is kept in the expanded detail.
      // A SECURE finding's severity is the rule's, not a live risk → muted.
      sorter: (a, b) =>
        (SEV_RANK[(a.realized_severity || a.severity || "").toUpperCase()] ?? 9) -
        (SEV_RANK[(b.realized_severity || b.severity || "").toUpperCase()] ?? 9),
      render: (_, f) => {
        const sev = f.realized_severity || f.severity;
        if (f.verdict === "SECURE") return <span className="text-xs text-text-muted">{sev || "—"}</span>;
        return <SeverityText tone={severityToTone(sev)}>{sev || "—"}</SeverityText>;
      },
    },
    {
      title: Th("Confidence"),
      key: "confidence",
      width: 120,
      sorter: (a, b) => (CONF_RANK[(a.confidence || "").toLowerCase()] ?? 9) - (CONF_RANK[(b.confidence || "").toLowerCase()] ?? 9),
      render: (_, f) => <ConfidencePill confidence={f.confidence} />,
    },
  ];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-baseline gap-1.5">
          <span className="font-semibold tabular-nums text-text-primary">{findings.length}</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">total</span>
        </span>
        <ToneTag tone="danger">{counts.v} vulnerable</ToneTag>
        <ToneTag tone="success">{counts.s} secure</ToneTag>
        {counts.u > 0 && (
          <ToneTag tone="muted" title="The analyzer reached no verdict — treat as unreviewed, not safe.">
            {counts.u} inconclusive
          </ToneTag>
        )}
        {counts.na > 0 && <ToneTag tone="muted">{counts.na} n/a</ToneTag>}
        {showFilter && (
          <div className="ml-auto">
            <Segmented
              size="small"
              value={filter}
              onChange={(v) => setFilter(v as typeof filter)}
              options={[
                { label: "All", value: "all" },
                { label: "Vulnerable", value: "vulnerable" },
                { label: "Secure", value: "secure" },
                ...(counts.u + counts.na > 0 ? [{ label: "Inconclusive", value: "other" }] : []),
              ]}
            />
          </div>
        )}
      </div>
      <Table<VMFinding>
        size="small"
        rowKey={(f) => `${f.rule_id}:${f.component_name ?? ""}`}
        dataSource={rows}
        pagination={false}
        columns={columns}
        scroll={{ x: 720 }}
        locale={{ emptyText: "No findings recorded." }}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys(keys as Key[]),
          expandRowByClick: true,
          expandIcon: ({ expanded, onExpand, record }) => (
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={expanded ? "Collapse finding detail" : "Expand finding detail"}
              onClick={(e) => onExpand(record, e)}
              style={{
                cursor: "pointer",
                display: "inline-flex",
                background: "transparent",
                border: "none",
                padding: 0,
                color: token.colorTextTertiary,
              }}
            >
              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
          ),
          expandedRowRender: (f) => <Expanded f={f} />,
        }}
      />
    </div>
  );
}
